import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import "./StatisticsModal.css";

const StatisticsModal = ({
  open,
  onClose,
  geoJsonResults,
  geoJsonInputContour,
  predictionHours,
  timeStep,
  isLightMode,
}) => {
  const canvasRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [animProgress, setAnimProgress] = useState(0);// 0→1 float driving the chart draw animation
  const [dataType, setDataType] = useState("burnedArea");// "burnedArea" or "buildings" (the dropdown)
  const [viewMode, setViewMode] = useState("perHour");  // "perHour" or "cumulative" (the dropdown)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0, show: false });
  const animFrameRef = useRef(null);
  const themeClass = isLightMode ? "light-theme" : "";

  // ── Time constants ────────────────────────────────────────────────────────
  const dtMinutesMap = { "1h": 60, "30min": 30, "45min": 45, "15min": 15 };
  const dtMinutes = dtMinutesMap[timeStep] || 60;
  const contoursPerHour = 60 / dtMinutes; 

  // ── Hour-boundary areas ───────────────────────────────────────────────────
  /*eg if you have 15-min steps across 7 hours, out of 28 results it picks results
   at index 3, 7, 11... (the last contour of each hour). 
  Converts from m² to hectares (/ 10000). */
  // Always by full hour regardless of timestep
  const hourBoundaryAreas = (() => {
    const pts = [];
    pts.push(
      geoJsonInputContour?.area != null ? geoJsonInputContour.area / 10000 : 0,
    );
    for (let h = 1; h <= predictionHours; h++) {
      const lastContourIndex = h * contoursPerHour - 1;
      const result = geoJsonResults[lastContourIndex];
      if (result?.area != null) {
        pts.push(result.area / 10000);
      } else {
        pts.push(pts[pts.length - 1]);
      }
    }
    return pts;
  })();

  // ── Per-hour burned area = diff of consecutive hour boundaries ────────────
  const perHourAreas = (() => {
    return hourBoundaryAreas.map((v, i) =>
      i === 0 ? 0 : Math.max(0, v - hourBoundaryAreas[i - 1]),
    );
  })();

  // ── Buildings per hour (sum all sub-contours in each hour, matching sidebar)
  const buildingsPerHour = (() => {
    const groups = new Array(predictionHours).fill(0);
    geoJsonResults.forEach((r, idx) => {
      const hourIndex = Math.floor(idx / contoursPerHour);
      if (hourIndex < predictionHours) {
        groups[hourIndex] += r?.countBuildings ?? 0;
      }
    });
    return groups;
  })();

  // ── Cumulative buildings ──────────────────────────────────────────────────
  const cumulativeBuildings = (() => {
    const cumul = [0]; // t0 = 0
    let sum = 0;
    for (let h = 0; h < predictionHours; h++) {
      sum += buildingsPerHour[h] ?? 0;
      cumul.push(sum);
    }
    return cumul;
  })();

  // perHourBuildings[i] = new buildings in hour i (i=0 → t0 = 0)
  const perHourBuildings = [0, ...buildingsPerHour];

  // ── Select active dataset ─────────────────────────────────────────────────
  const getCurrentData = () => {
    if (dataType === "burnedArea")
      return viewMode === "cumulative" ? hourBoundaryAreas : perHourAreas;
    return viewMode === "cumulative" ? cumulativeBuildings : perHourBuildings;
  };

  // ── Y-axis label ──────────────────────────────────────────────────────────
  const getYAxisLabel = () => {
    if (dataType === "burnedArea")
      return viewMode === "cumulative"
        ? "Surface brûlée cumulée (ha)"
        : "Surface brûlée par heure (ha)";
    return viewMode === "cumulative"
      ? "Nombre de bâtiments cumulé"
      : "Nombre de bâtiments par heure";
  };

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const getCurrentKPIs = () => {
    if (dataType === "burnedArea") {
      const first = hourBoundaryAreas[0];
      const last = hourBoundaryAreas[hourBoundaryAreas.length - 1];
      const avgRate =
        predictionHours > 0
          ? ((last - first) / predictionHours).toFixed(2)
          : "0";
      return {
        label1: "Surface initiale",
        value1: first.toFixed(2),
        unit1: "ha",
        label2: "Surface totale brûlée",
        value2: last.toFixed(2),
        unit2: "ha",
        label3: "Progression totale",
        value3: `+${(last - first).toFixed(2)}`,
        unit3: "ha",
        label4: "Vitesse moyenne",
        value4: avgRate,
        unit4: "ha/h",
      };
    }
    const total = cumulativeBuildings[cumulativeBuildings.length - 1];
    return {
      label1: "Bâtiments à risque (total)",
      value1: total,
      unit1: "bât.",
      label2: null,
      value2: null,
      unit2: null,
      label3: null,
      value3: null,
      unit3: null,
      label4: null,
      value4: null,
      unit4: null,
    };
  };

  // ── Data points — one per full hour (t0, t+1h, t+2h, ...) ────────────────
  const getFilteredDataPoints = () => {
    const currentData = getCurrentData();
    return currentData.map((value, i) => ({
      label: i === 0 ? "t0" : `t+${i}h`,
      value,
      cumulativeArea: hourBoundaryAreas[i] ?? 0,
      perHourArea: perHourAreas[i] ?? 0,
      cumulativeBuildings: cumulativeBuildings[i] ?? 0,
      perHourBuildings: perHourBuildings[i] ?? 0,
      index: i,
      timeHours: i,
    }));
  };

  const dataPoints = getFilteredDataPoints();

  // rawDataPoints same as dataPoints for chart line (hour-indexed)
  const rawDataPoints = (() => {
    const currentData = getCurrentData();
    return currentData.map((value, i) => ({
      value,
      timeHours: i,
    }));
  })();

  const maxValue = Math.max(...dataPoints.map((p) => p.value), 0.1);
  const kpis = getCurrentKPIs();

  // ── Animations ────────────────────────────────────────────────────────────
  // First: resets animation when modal opens/closes
  useEffect(() => {
    if (!open) {
      setAnimProgress(0);
      cancelAnimationFrame(animFrameRef.current);
      return;
    }
    let start = null;
    const duration = 900;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setAnimProgress(1 - Math.pow(1 - p, 3));
      if (p < 1) animFrameRef.current = requestAnimationFrame(step);
    };
    animFrameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [open]);
// Second: re-triggers animation when user changes dataType or viewMode
  useEffect(() => {
    if (open) {
      setAnimProgress(0);
      let start = null;
      const duration = 600;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / duration, 1);
        setAnimProgress(1 - Math.pow(1 - p, 3));
        if (p < 1) animFrameRef.current = requestAnimationFrame(step);
      };
      animFrameRef.current = requestAnimationFrame(step);
      return () => cancelAnimationFrame(animFrameRef.current);
    }
  }, [dataType, viewMode, open]);

  // ── Y-label formatter ─────────────────────────────────────────────────────
  const formatYLabel = (val) => {
    if (dataType === "buildings") return Math.round(val).toString();
    return val.toFixed(1);
  };

  // ── Draw chart ────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !open) return;
    const labelColor = getComputedStyle(canvas)
      .getPropertyValue("--label-color")
      .trim();
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    if (W === 0 || H === 0) return;

    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const PAD = { top: 28, right: 24, bottom: 58, left: 62 };
    const chartW = W - PAD.left - PAD.right;
    const chartH = H - PAD.top - PAD.bottom;
    const maxTime = predictionHours;
    const allValues = [
      ...rawDataPoints.map((p) => p.value),
      ...dataPoints.map((p) => p.value),
    ];
    const maxVal = Math.max(...allValues);
    const yMax = maxVal * 1.18;
    const toX = (timeHours) => PAD.left + (timeHours / maxTime) * chartW;
    const toY = (v) => PAD.top + chartH - (v / yMax) * chartH;

    ctx.clearRect(0, 0, W, H);

    // Grid lines + Y labels
    const gridLines = 5;
    for (let g = 0; g <= gridLines; g++) {
      const y = PAD.top + (g / gridLines) * chartH;
      const val = (yMax * (gridLines - g)) / gridLines;
      ctx.beginPath();
      ctx.strokeStyle = "rgba(255,255,255,0.055)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 5]);
      ctx.moveTo(PAD.left, y);
      ctx.lineTo(PAD.left + chartW, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = labelColor;
      ctx.font = "bold 12px 'Courier New', monospace";
      ctx.textAlign = "right";
      ctx.fillText(formatYLabel(val), PAD.left - 7, y + 3.5);
    }

    // Axes
    ctx.beginPath();
    ctx.strokeStyle = "rgba(255,255,255,0.13)";
    ctx.lineWidth = 1;
    ctx.moveTo(PAD.left, PAD.top);
    ctx.lineTo(PAD.left, PAD.top + chartH);
    ctx.lineTo(PAD.left + chartW, PAD.top + chartH);
    ctx.stroke();

    // Y-axis label
    ctx.save();
    ctx.translate(13, PAD.top + chartH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = labelColor;
    ctx.font = "bold 12px 'Courier New', monospace";
    ctx.textAlign = "center";
    ctx.fillText(getYAxisLabel(), 0, 0);
    ctx.restore();

    // X-axis label
    ctx.fillStyle = labelColor;
    ctx.font = "bold 12px 'Courier New', monospace";
    ctx.textAlign = "center";
    ctx.fillText("Temps (heures)", PAD.left + chartW / 2, H - 12);

    if (rawDataPoints.length < 2) return;

    const visibleRawPoints = rawDataPoints.filter(
      (p) => p.timeHours <= maxTime * animProgress,
    );
    if (visibleRawPoints.length >= 2) {
      const grad = ctx.createLinearGradient(0, PAD.top, 0, PAD.top + chartH);
      grad.addColorStop(0, "rgba(225,6,19,0.38)");
      grad.addColorStop(0.55, "rgba(177,5,17,0.10)");
      grad.addColorStop(1, "rgba(177,5,17,0)");

      ctx.beginPath();
      visibleRawPoints.forEach((pt, i) => {
        const x = toX(pt.timeHours);
        const y = toY(pt.value);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.lineTo(
        toX(visibleRawPoints[visibleRawPoints.length - 1].timeHours),
        PAD.top + chartH,
      );
      ctx.lineTo(toX(visibleRawPoints[0].timeHours), PAD.top + chartH);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();

      ctx.beginPath();
      visibleRawPoints.forEach((pt, i) => {
        const x = toX(pt.timeHours);
        const y = toY(pt.value);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = "#e10613";
      ctx.lineWidth = 2.5;
      ctx.shadowColor = "rgba(225,6,19,0.55)";
      ctx.shadowBlur = 9;
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // X-axis hour labels
    for (let hour = 0; hour <= Math.floor(maxTime); hour++) {
      const x = toX(hour);
      ctx.fillStyle = labelColor;
      ctx.font = "bold 12px 'Courier New', monospace";
      ctx.textAlign = "center";
      ctx.fillText(hour === 0 ? "t0" : `t+${hour}h`, x, PAD.top + chartH + 18);
    }

    const visibleDataPoints = dataPoints.filter(
      (p) => p.timeHours <= maxTime * animProgress,
    );
    visibleDataPoints.forEach((pt) => {
      const x = toX(pt.timeHours);
      const y = toY(pt.value);
      const originalIndex = dataPoints.findIndex(
        (d) => d.timeHours === pt.timeHours,
      );
      const isHov = hoveredIndex === originalIndex;

      ctx.beginPath();
      ctx.arc(x, y, isHov ? 8 : 5, 0, Math.PI * 2);
      ctx.fillStyle = isHov ? "#ffffff" : "#e10613";
      ctx.shadowColor = "rgba(225,6,19,0.8)";
      ctx.shadowBlur = isHov ? 20 : 10;
      ctx.fill();

      if (isHov) {
        ctx.beginPath();
        ctx.arc(x, y, 12, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(225,6,19,0.2)";
        ctx.fill();
      }
      ctx.shadowBlur = 0;
    });
  }, [
    dataPoints,
    rawDataPoints,
    animProgress,
    hoveredIndex,
    maxValue,
    open,
    dataType,
    viewMode,
    predictionHours,
  ]);

  // ── Mouse interaction ─────────────────────────────────────────────────────
  //On every mouse move over the canvas, it calculates which data point is closest (within 30px). 
  // If found, it sets hoveredIndex and calculates where to position the tooltip popup.
  const handleCanvasMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const PAD_LEFT = 62,
      PAD_RIGHT = 24;
    const chartW = canvas.offsetWidth - PAD_LEFT - PAD_RIGHT;
    const maxTime = predictionHours;

    let nearest = null,
      minDist = 30;
    dataPoints.forEach((pt, i) => {
      const x = PAD_LEFT + (pt.timeHours / maxTime) * chartW;
      const d = Math.abs(mx - x);
      if (d < minDist) {
        minDist = d;
        nearest = i;
      }
    });

    if (minDist < 30 && nearest !== null) {
      setHoveredIndex(nearest);
      const x = PAD_LEFT + (dataPoints[nearest].timeHours / maxTime) * chartW;
      const rect2 = canvas.getBoundingClientRect();
      const H2 = canvas.offsetHeight;
      const chartH2 = H2 - 28 - 58;
      const yMax2 = maxValue * 1.18;
      const val = dataPoints[nearest].value;
      const y = 28 + chartH2 - (val / yMax2) * chartH2;

      let tooltipX = rect2.left + x + 15;
      let tooltipY = rect2.top + y - 50;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (tooltipX + 250 > viewportWidth) tooltipX = rect2.left + x - 265;
      if (tooltipY < 10) tooltipY = rect2.top + y + 20;
      if (tooltipY + 200 > viewportHeight) tooltipY = viewportHeight - 210;

      setTooltipPos({ x: tooltipX, y: tooltipY, show: true });
    } else {
      setHoveredIndex(null);
      setTooltipPos({ ...tooltipPos, show: false });
    }
  };

  if (!open) return null;

  const hoveredPoint = hoveredIndex !== null ? dataPoints[hoveredIndex] : null;

  return ReactDOM.createPortal(
    <div className={`stats-modal-overlay ${themeClass}`} onClick={onClose} data-testid="statistics-modal">
      <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stats-modal-header">
          <div className="stats-modal-title" data-testid="statistics-modal-title">
            <LocalFireDepartmentIcon
              style={{ color: "#e10613", fontSize: 18 }}
            />
            <span>Statistiques de propagation</span>
          </div>
          <div className="stats-modal-actions">
            <select
              className="stats-view-select"
              value={dataType}
              onChange={(e) => setDataType(e.target.value)}
                        data-testid="statistics-indicator-select"

            >
              <option value="burnedArea" data-testid="statistics-indicator-burned-area">Surface brûlée</option>
              <option value="buildings" data-testid="statistics-indicator-at-risk-buildings">Bâtiments à risque</option>
            </select>
            <select
              className="stats-view-select"
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
               data-testid="statistics-method-select"
            >
              <option value="perHour" data-testid="statistics-method-by-interval">Par intervalle</option>
              <option value="cumulative" data-testid="statistics-method-cumulative">Cumulé</option>
            </select>
            <button
              className="stats-modal-close"
              onClick={onClose}
              type="button"
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>

        <div className="stats-modal-content">
          <div className="stats-kpi-row" data-testid="statistics-kpi-panel">
            <div className="stats-kpi-card">
              <span className="stats-kpi-label">{kpis.label1}</span>
              <span className="stats-kpi-value">
                {kpis.value1}
                <span className="stats-kpi-unit"> {kpis.unit1}</span>
              </span>
            </div>
            {kpis.label2 && (
              <div className="stats-kpi-card">
                <span className="stats-kpi-label">{kpis.label2}</span>
                <span className="stats-kpi-value">
                  {kpis.value2}
                  <span className="stats-kpi-unit"> {kpis.unit2}</span>
                </span>
              </div>
            )}
            {kpis.label3 && (
              <div className="stats-kpi-card">
                <span className="stats-kpi-label">{kpis.label3}</span>
                <span className="stats-kpi-value">
                  {kpis.value3}
                  <span className="stats-kpi-unit"> {kpis.unit3}</span>
                </span>
              </div>
            )}
            {kpis.label4 && (
              <div className="stats-kpi-card">
                <span className="stats-kpi-label">{kpis.label4}</span>
                <span className="stats-kpi-value">
                  {kpis.value4}
                  <span className="stats-kpi-unit"> {kpis.unit4}</span>
                </span>
              </div>
            )}
          </div>

          <div className="stats-chart-wrapper">
            <canvas
              ref={canvasRef}
              className="stats-canvas"
                        data-testid="statistics-chart"

              onMouseMove={handleCanvasMouseMove}
              onMouseLeave={() => {
                setHoveredIndex(null);
                setTooltipPos({ ...tooltipPos, show: false });
              }}
            />
          </div>
        </div>

        {tooltipPos.show && hoveredPoint && (
          <div
            className={`stats-tooltip stats-tooltip--${viewMode}`}
                    data-testid="statistics-chart-tooltip"

            style={{ left: tooltipPos.x + 15, top: tooltipPos.y - 50 }}
          >
            <div className="stats-tooltip-header">{hoveredPoint.label}</div>

            {dataType === "burnedArea" ? (
              viewMode === "cumulative" ? (
                <>
                  <div className="stats-tooltip-row">
                    <span className="stats-tooltip-label">
                      {hoveredPoint.index === 0
                        ? "Surface initiale :"
                        : "Surface cumulée :"}
                    </span>
                    <span className="stats-tooltip-value stats-tooltip-value--primary">
                      {hoveredPoint.value.toFixed(2)} ha
                    </span>
                  </div>
                  {hoveredPoint.index > 0 && (
                    <div className="stats-tooltip-row stats-tooltip-row--secondary">
                      <span className="stats-tooltip-label">
                        Brûlé sur cette heure :
                      </span>
                      <span className="stats-tooltip-value stats-tooltip-value--accent">
                        +{hoveredPoint.perHourArea.toFixed(2)} ha
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {hoveredPoint.index === 0 ? (
                    <div>
                      <div
                        className="stats-tooltip-row"
                        style={{ marginTop: "8px" }}
                      >
                        <span className="stats-tooltip-label">
                          Surface initiale :
                        </span>
                        <span className="stats-tooltip-value stats-tooltip-value--primary">
                          {(geoJsonInputContour?.area
                            ? geoJsonInputContour.area / 10000
                            : 0
                          ).toFixed(2)}{" "}
                          ha
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="stats-tooltip-row">
                        <span className="stats-tooltip-label">
                          Brûlé cette heure :
                        </span>
                        <span className="stats-tooltip-value stats-tooltip-value--primary">
                          {hoveredPoint.value.toFixed(2)} ha
                        </span>
                      </div>
                      <div className="stats-tooltip-row stats-tooltip-row--secondary">
                        <span className="stats-tooltip-label">
                          Cumulatif jusqu'ici :
                        </span>
                        <span className="stats-tooltip-value stats-tooltip-value--accent">
                          {hoveredPoint.cumulativeArea.toFixed(2)} ha
                        </span>
                      </div>
                    </>
                  )}
                </>
              )
            ) : viewMode === "cumulative" ? (
              <>
                <div className="stats-tooltip-row">
                  <span className="stats-tooltip-label">
                    Bâtiments cumulés :
                  </span>
                  <span className="stats-tooltip-value stats-tooltip-value--primary">
                    {Math.round(hoveredPoint.value)} bât.
                  </span>
                </div>
                {hoveredPoint.index > 0 && (
                  <div className="stats-tooltip-row stats-tooltip-row--secondary">
                    <span className="stats-tooltip-label">
                      Nouveaux cette heure :
                    </span>
                    <span className="stats-tooltip-value stats-tooltip-value--accent">
                      +{hoveredPoint.perHourBuildings} bât.
                    </span>
                  </div>
                )}
              </>
            ) : (
              <>
                {hoveredPoint.index === 0 ? (
                  <div className="stats-tooltip-start">
                    Point de départ de l'incendie
                  </div>
                ) : (
                  <>
                    <div className="stats-tooltip-row">
                      <span className="stats-tooltip-label">
                        Nouveaux bâtiments :
                      </span>
                      <span className="stats-tooltip-value stats-tooltip-value--primary">
                        {Math.round(hoveredPoint.value)} bât.
                      </span>
                    </div>
                    <div className="stats-tooltip-row stats-tooltip-row--secondary">
                      <span className="stats-tooltip-label">
                        Cumulatif jusqu'ici :
                      </span>
                      <span className="stats-tooltip-value stats-tooltip-value--accent">
                        {hoveredPoint.cumulativeBuildings} bât.
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default StatisticsModal;
